import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Activity, AlertCircle, CheckCircle, Clock, RefreshCw, Play } from "lucide-react";
import { toast } from "sonner";

/**
 * Widget dashboard "Sync Health" por scraper de licitaciones.
 *
 * Consume GET /api/admin/sync-health/by-source y muestra una fila por fuente
 * con semáforo verde/amarillo/rojo, contadores 7d y último run.
 * Solo visible para admins.
 */
const HEALTH_META = {
  green:   { bg: "bg-emerald-500",  txt: "text-emerald-700", ring: "ring-emerald-200", label: "OK"       },
  yellow:  { bg: "bg-amber-400",    txt: "text-amber-700",   ring: "ring-amber-200",   label: "Retraso" },
  red:     { bg: "bg-red-500",      txt: "text-red-700",     ring: "ring-red-200",     label: "Crítico" },
  unknown: { bg: "bg-gray-300",     txt: "text-gray-600",    ring: "ring-gray-200",    label: "N/D"      },
};

const fmtRelTime = (iso) => {
  if (!iso) return "nunca";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffHours = (Date.now() - d.getTime()) / 3_600_000;
  if (diffHours < 1) return `hace ${Math.max(1, Math.round(diffHours * 60))} min`;
  if (diffHours < 48) return `hace ${Math.round(diffHours)} h`;
  return `hace ${Math.round(diffHours / 24)} d`;
};

export function SyncHealthByScraper() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(null);   // source key en curso

  const fetchData = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/admin/sync-health/by-source`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const triggerSync = async (source) => {
    if (syncing) return;
    setSyncing(source);
    try {
      // Endpoint estándar: /api/tender360/m0/integrations/{source}/sync
      await axios.post(`${API}/tender360/m0/integrations/${source}/sync`);
      toast.success(`Sync '${source}' iniciado`, {
        description: "Ejecutándose en segundo plano. Refresco automático en 20s.",
        duration: 4000,
      });
      setTimeout(() => fetchData(), 20_000);
    } catch (e) {
      toast.error(`Error al iniciar sync '${source}'`, {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setSyncing(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="mt-3 p-3 rounded-xl bg-white border border-gray-100 text-xs text-gray-500 flex items-center gap-2">
        <RefreshCw size={12} className="animate-spin" /> Cargando estado de scrapers…
      </div>
    );
  }
  if (err) {
    // Silencioso si no eres admin (403)
    if (String(err).toLowerCase().includes("admin")) return null;
    return (
      <div className="mt-3 p-2 rounded-lg bg-red-50 border border-red-100 text-[11px] text-red-700 flex items-center gap-1.5">
        <AlertCircle size={11} /> Sync Health: {err}
      </div>
    );
  }
  if (!data) return null;

  const { sources = [], summary = {}, checked_at } = data;

  return (
    <div
      className="mt-3 p-3 rounded-xl bg-white border border-gray-100"
      data-testid="sync-health-by-scraper"
    >
      {/* Header con resumen */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-indigo-600" />
          <p className="text-xs font-bold text-gray-800 font-['Outfit']">Sync Health por fuente</p>
          <span className="text-[10px] text-gray-400">
            (actualizado {fmtRelTime(checked_at)})
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          {summary.green   > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />{summary.green} OK</span>}
          {summary.yellow  > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />{summary.yellow} Retraso</span>}
          {summary.red     > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{summary.red} Crítico</span>}
          {summary.unknown > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" />{summary.unknown} N/D</span>}
          <button
            onClick={fetchData}
            data-testid="sync-health-refresh"
            className="ml-1 p-1 rounded hover:bg-gray-100 text-gray-500"
            title="Refrescar"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Grid de scrapers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {sources.map(s => {
          const h = HEALTH_META[s.health] || HEALTH_META.unknown;
          return (
            <div
              key={s.source}
              data-testid={`sync-health-row-${s.source}`}
              className={`p-2.5 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={`w-2 h-2 rounded-full ${h.bg} ring-2 ${h.ring} flex-shrink-0`} title={h.label} />
                  <span className="text-[11px] font-bold text-gray-800 truncate">
                    {s.icon} {s.label}
                  </span>
                </div>
                <span className={`text-[9px] font-bold uppercase ${h.txt}`}>{h.label}</span>
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-500">
                <div className="flex items-center gap-1">
                  <Clock size={9} />
                  <span>{fmtRelTime(s.last_run_at)}</span>
                  {s.last_run_status === "error" && <AlertCircle size={9} className="text-red-500 ml-0.5" />}
                  {s.last_run_status === "ok"    && <CheckCircle size={9} className="text-emerald-500 ml-0.5" />}
                </div>
                <div className="text-right text-gray-600">
                  <span className="font-semibold">{s.total_docs.toLocaleString("es-ES")}</span>
                  <span className="text-[9px] text-gray-400"> docs</span>
                </div>
              </div>
              {(s.runs_last_7d > 0 || s.added_last_7d > 0) && (
                <div className="mt-1 text-[9px] text-gray-400 flex items-center gap-1.5 flex-wrap">
                  <span>7d: {s.runs_last_7d} runs</span>
                  {s.errors_last_7d > 0 && <span className="text-red-500 font-semibold">· {s.errors_last_7d} err</span>}
                  {s.added_last_7d > 0 && <span className="text-emerald-600 font-semibold">· +{s.added_last_7d} nuevos</span>}
                </div>
              )}
              {s.last_run_error && (
                <p className="mt-1 text-[9px] text-red-500 italic truncate" title={s.last_run_error}>
                  ⚠ {s.last_run_error}
                </p>
              )}
              {/* Botón "Ejecutar sync ahora" — visible en filas críticas o retraso
                  cuando la fuente tiene endpoint disponible */}
              {(s.health === "red" || s.health === "yellow") && s.endpoint && (
                <button
                  onClick={() => triggerSync(s.source)}
                  disabled={syncing === s.source}
                  data-testid={`sync-now-${s.source}`}
                  className="mt-2 w-full text-[10px] font-semibold py-1 px-2 rounded-md
                             bg-gradient-to-r from-indigo-500 to-purple-500 text-white
                             hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50
                             flex items-center justify-center gap-1 transition-colors"
                >
                  {syncing === s.source ? (
                    <><RefreshCw size={9} className="animate-spin" /> Ejecutando…</>
                  ) : (
                    <><Play size={9} /> Ejecutar sync ahora</>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
